import React, { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, label, btn, btnGhost } from "../drawings/_drawingsCommon";

const ALL_CATS = ["safety", "operability", "constructability", "standards", "cost"];

const CAT_COLORS = {
  safety: "#ef4444",
  operability: "#f59e0b",
  constructability: "#3aa0ff",
  standards: "#84cc16",
  cost: "#a855f7",
};

const SEV_COLORS = { critical: "#ef4444", major: "#f59e0b", minor: "#84cc16" };

export default function DesignReviewerPanel({ projectId }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [runAt, setRunAt] = useState(null);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedCats, setSelectedCats] = useState(ALL_CATS);

  const loadLast = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/ai/review/last`);
      if (data?.result) {
        setResult(data.result);
        setRunAt(data.run_at);
      }
    } catch { /* empty */ }
  };

  useEffect(() => { loadLast(); }, [projectId]);

  const runReview = async () => {
    setBusy(true); setErr(""); setResult(null); setRunAt(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/review`,
        { categories: selectedCats });
      setResult(data);
      setRunAt(new Date().toISOString());
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    } finally { setBusy(false); }
  };

  const findings = (result?.findings || []).filter((f) => {
    if (filter === "all") return true;
    if (filter === "critical" || filter === "major" || filter === "minor")
      return f.severity === filter;
    return f.category === filter;
  });

  return (
    <div data-testid="review-panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ ...card, flexDirection: "row", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ ...label, marginBottom: 2 }}>AI Design Reviewer — Capexara Process Industry AI</div>
          <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>
            Senior-engineer-level review across safety, operability, constructability, standards and cost.
            {runAt && <> · Last run: {new Date(runAt).toLocaleString()}</>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {ALL_CATS.map((c) => {
            const on = selectedCats.includes(c);
            return (
              <button key={c} data-testid={`review-cat-${c}`}
                style={{ ...btnGhost, padding: "4px 10px",
                          background: on ? CAT_COLORS[c] + "33" : "transparent",
                          borderColor: on ? CAT_COLORS[c] : "var(--pf-border)" }}
                onClick={() => setSelectedCats(on
                  ? selectedCats.filter((x) => x !== c)
                  : [...selectedCats, c])}>{c}</button>
            );
          })}
        </div>
        <button style={btn} data-testid="review-run" onClick={runReview} disabled={busy || selectedCats.length === 0}>
          {busy ? "Reviewing… (90-180 s)" : "Run AI Review"}
        </button>
      </div>

      {err && <div style={{ padding: 8, color: "#FF8888", background: "rgba(239,68,68,.1)", borderRadius: 4, fontSize: 12 }}>{err}</div>}

      {result && (
        <>
          {result.summary && (
            <div style={{ ...card, background: "rgba(242,107,31,.05)", borderColor: "rgba(242,107,31,.3)" }}>
              <div style={label}>Summary</div>
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>{result.summary}</div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
            <Kpi label="Findings" value={result.kpis?.total_findings || 0} testid="review-kpi-total" />
            <Kpi label="Critical" value={result.kpis?.critical || 0} color={SEV_COLORS.critical} testid="review-kpi-critical" />
            <Kpi label="Major" value={result.kpis?.major || 0} color={SEV_COLORS.major} testid="review-kpi-major" />
            <Kpi label="Minor" value={result.kpis?.minor || 0} color={SEV_COLORS.minor} testid="review-kpi-minor" />
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[["all", "All"], ...ALL_CATS.map((c) => [c, c]),
              ["critical", "Critical"], ["major", "Major"], ["minor", "Minor"]].map(([k, l]) => (
              <button key={k} data-testid={`review-filter-${k}`}
                style={filter === k ? btn : btnGhost} onClick={() => setFilter(k)}>{l}</button>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {findings.map((f, i) => (
              <div key={i} style={card} data-testid={`review-finding-${i}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 3,
                                   background: (CAT_COLORS[f.category] || "#888") + "30",
                                   color: CAT_COLORS[f.category] || "#888", fontWeight: 700,
                                   textTransform: "uppercase" }}>{f.category}</span>
                    <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 3,
                                   background: (SEV_COLORS[f.severity] || "#888") + "30",
                                   color: SEV_COLORS[f.severity] || "#888", fontWeight: 700,
                                   textTransform: "uppercase" }}>{f.severity}</span>
                    <strong style={{ fontSize: 13 }}>{f.title}</strong>
                  </div>
                  {f.standard && <span style={{ fontSize: 10, color: "var(--pf-muted)", fontFamily: "monospace" }}>{f.standard}</span>}
                </div>
                <div style={{ fontSize: 12, color: "var(--pf-fg)" }}>{f.description}</div>
                {f.recommendation && (
                  <div style={{ marginTop: 6, padding: 6, background: "var(--pf-surface-2)", borderRadius: 4, fontSize: 11 }}>
                    <strong style={{ color: "#22c55e" }}>Recommend:</strong> {f.recommendation}
                  </div>
                )}
                {f.tags_affected && f.tags_affected.length > 0 && (
                  <div style={{ marginTop: 4, fontSize: 10, color: "var(--pf-muted)" }}>
                    Affects: {f.tags_affected.join(", ")}
                  </div>
                )}
              </div>
            ))}
            {findings.length === 0 && (
              <div style={{ ...card, textAlign: "center", color: "var(--pf-muted)", padding: 24 }}>
                No findings match this filter.
              </div>
            )}
          </div>
        </>
      )}

      {!result && !busy && (
        <div style={{ ...card, textAlign: "center", color: "var(--pf-muted)", padding: 24 }}>
          Click "Run AI Review" to have Capexara Process Industry AI critique the entire design.
        </div>
      )}
    </div>
  );
}

function Kpi({ label: l, value, color, testid }) {
  return (
    <div data-testid={testid} style={{ padding: 10, background: "var(--pf-surface)", border: "1px solid var(--pf-border)", borderRadius: 6 }}>
      <div style={{ fontSize: 9, color: "var(--pf-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>{l}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || "#F26B1F", marginTop: 2 }}>{value}</div>
    </div>
  );
}
